import {
  Group,
  IcosahedronGeometry,
  ShaderMaterial,
  Mesh,
  Color,
  TorusGeometry,
  MeshBasicMaterial,
  PointLight,
  SphereGeometry,
  InstancedMesh,
  Object3D,
  Matrix4,
} from 'three';
import { COLORS, PLATFORM } from '../constants';
import type { AgentState } from '../types';
import holographicVert from '../shaders/holographic.vert?raw';
import holographicFrag from '../shaders/holographic.frag?raw';

const PARTICLE_COUNT = 20;
// Reusable objects to avoid GC pressure
const _dummy = new Object3D();

export class AgentRenderer {
  static createAgentMesh(agent: AgentState): Group {
    const group = new Group();
    group.userData.agentId = agent.id;

    // Invisible hitbox — generous click area
    const hitGeo = new SphereGeometry(1.2, 8, 8);
    const hitMat = new MeshBasicMaterial({ visible: false });
    const hitbox = new Mesh(hitGeo, hitMat);
    hitbox.position.y = 1.0;
    group.add(hitbox);

    // Main body — icosahedron with holographic shader
    const bodyGeo = new IcosahedronGeometry(0.65, 1);
    const bodyMat = new ShaderMaterial({
      vertexShader: holographicVert,
      fragmentShader: holographicFrag,
      uniforms: {
        uTime: { value: 0 },
        uDrift: { value: 0 },
        uBaseColor: { value: new Color(COLORS.healthy) },
        uDriftColor: { value: new Color(COLORS.driftHigh) },
        uOpacity: { value: 0.9 },
      },
      transparent: true,
      depthWrite: false,
    });
    const body = new Mesh(bodyGeo, bodyMat);
    body.position.y = 1.0;
    body.name = 'body';
    group.add(body);

    // Drift ring — torus at the base
    const ringGeo = new TorusGeometry(0.6, 0.03, 8, 32);
    const ringMat = new MeshBasicMaterial({
      color: COLORS.healthy,
      transparent: true,
      opacity: 0.5,
    });
    const ring = new Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.3;
    ring.name = 'driftRing';
    group.add(ring);

    // Status light
    const light = new PointLight(COLORS.healthy, 1, 5);
    light.position.y = 1.0;
    light.name = 'statusLight';
    group.add(light);

    // Particle emitter (small instanced spheres orbiting the agent)
    const particleGeo = new SphereGeometry(0.02, 4, 4);
    const particleMat = new MeshBasicMaterial({
      color: COLORS.healthy,
      transparent: true,
      opacity: 0.6,
    });
    const particles = new InstancedMesh(particleGeo, particleMat, PARTICLE_COUNT);
    particles.name = 'particles';
    const dummy = new Object3D();
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const angle = (i / PARTICLE_COUNT) * Math.PI * 2;
      const r = 0.5 + Math.random() * 0.3;
      const y = 0.5 + Math.random() * 1.0;
      dummy.position.set(Math.cos(angle) * r, y, Math.sin(angle) * r);
      dummy.updateMatrix();
      particles.setMatrixAt(i, dummy.matrix);
    }
    particles.instanceMatrix.needsUpdate = true;
    group.add(particles);

    // Cooldown indicator ring (invisible by default)
    const cooldownGeo = new TorusGeometry(0.7, 0.02, 8, 32);
    const cooldownMat = new MeshBasicMaterial({
      color: 0x60a5fa,
      transparent: true,
      opacity: 0,
    });
    const cooldown = new Mesh(cooldownGeo, cooldownMat);
    cooldown.rotation.x = -Math.PI / 2;
    cooldown.position.y = 0.3;
    cooldown.name = 'cooldownRing';
    group.add(cooldown);

    // Position on the arc
    const pos = AgentRenderer.getSlotPosition(agent.slotIndex);
    group.position.set(pos.x, 0, pos.z);

    return group;
  }

  static getSlotPosition(slotIndex: number): { x: number; z: number } {
    const totalSlots = PLATFORM.maxAgents;
    const angleSpan = Math.PI * 1.5; // 270 degrees of arc
    const startAngle = -Math.PI * 0.75; // start from the back-left
    const angle = startAngle + (slotIndex / (totalSlots - 1)) * angleSpan;
    return {
      x: Math.cos(angle) * PLATFORM.agentArcRadius,
      z: Math.sin(angle) * PLATFORM.agentArcRadius,
    };
  }

  static updateAgentVisuals(agent: AgentState, time: number): void {
    if (!agent.mesh) return;

    const body = agent.mesh.getObjectByName('body') as Mesh;
    const driftRing = agent.mesh.getObjectByName('driftRing') as Mesh;
    const statusLight = agent.mesh.getObjectByName('statusLight') as PointLight;
    const particles = agent.mesh.getObjectByName('particles') as InstancedMesh;
    const cooldownRing = agent.mesh.getObjectByName('cooldownRing') as Mesh;

    if (!body || !driftRing || !statusLight || !particles) return;

    const drift = agent.drift;
    const mat = body.material as ShaderMaterial;

    // Update shader uniforms
    mat.uniforms.uTime.value = time;
    mat.uniforms.uDrift.value = drift;

    // Drift ring color and scale
    const ringMat = driftRing.material as MeshBasicMaterial;
    // Interpolate drift color without allocating new Color objects
    const driftR = drift < 0.5 ? 0.306 + drift * 2 * (0.984 - 0.306) : 0.984 + (drift - 0.5) * 2 * (1.0 - 0.984);
    const driftG = drift < 0.5 ? 0.804 + drift * 2 * (0.573 - 0.804) : 0.573 + (drift - 0.5) * 2 * (0.420 - 0.573);
    const driftB = drift < 0.5 ? 0.769 + drift * 2 * (0.235 - 0.769) : 0.235 + (drift - 0.5) * 2 * (0.420 - 0.235);
    ringMat.color.setRGB(driftR, driftG, driftB);
    ringMat.opacity = 0.3 + drift * 0.5;
    driftRing.scale.setScalar(1 + drift * 0.3);

    // Status light color
    statusLight.color.setRGB(driftR, driftG, driftB);
    statusLight.intensity = 0.8 + drift * 1.5;

    // Particle animation (reuse _dummy to avoid allocations)
    const particleMat = particles.material as MeshBasicMaterial;
    particleMat.color.setRGB(driftR, driftG, driftB);
    particleMat.opacity = 0.4;
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const baseAngle = (i / PARTICLE_COUNT) * Math.PI * 2;
      const speed = 0.5 + drift * 1.5;
      const angle = baseAngle + time * speed;
      const r = 0.5 + Math.sin(time * 0.5 + i) * 0.12;
      const y = 0.5 + Math.sin(time * 0.3 + i * 0.5) * 0.4;

      _dummy.position.set(Math.cos(angle) * r, y, Math.sin(angle) * r);
      _dummy.scale.setScalar(0.7);
      _dummy.updateMatrix();
      particles.setMatrixAt(i, _dummy.matrix);
    }
    particles.instanceMatrix.needsUpdate = true;

    // Body hover animation
    body.position.y = 1.0 + Math.sin(time * 1.5) * 0.1;
    body.rotation.y = time * 0.3;

    // Cooldown ring
    if (cooldownRing) {
      const cooldownMat = cooldownRing.material as MeshBasicMaterial;
      if (agent.correctionCooldown > 0) {
        cooldownMat.opacity = 0.4;
        cooldownRing.scale.setScalar(1 + Math.sin(time * 3) * 0.05);
      } else {
        cooldownMat.opacity = 0;
      }
    }
  }
}
